import { DAEMON_URL } from "../config";
import type { Issue } from "./issues";

/**
 * Handing Bob's findings to Peter. Any project member may file; filing starts
 * nothing until an admin presses Start. The repo is the daemon's to choose.
 */

export type PeterIssue = Issue & {
  base_branch?: string | null;
  pr_url?: string | null;
  pr_reason?: string | null;
  run_outcome?: string | null;
  commit_sha?: string | null;
};

export interface ExistingFiling {
  findingId: string;
  issueId: number;
  baseBranch: string;
  status: string;
}

/** Filing an already-filed finding lands in `existing`, never an error. */
export interface FromBobResult {
  repo: string;
  bobBranch: string;
  baseBranch: string;
  created: PeterIssue[];
  existing: ExistingFiling[];
  notFound: string[];
  solved: string[];
  malformed: number;
  started: number;
}

export function refusalText(status: number, body: unknown): string {
  const b = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
  if (typeof b.message === "string" && b.message) return b.message;
  if (typeof b.error === "string" && b.error) return b.error;
  return `http ${status}`;
}

/** A refusal from the daemon, carrying its `error` code so a caller can tell a final no from a retryable one. */
export class PeterRefusal extends Error {
  constructor(message: string, readonly code: string | null) {
    super(message);
  }
}

/** The base is a protected branch: asking again cannot succeed. */
export function isProtectedBaseRefusal(e: unknown): boolean {
  return e instanceof PeterRefusal && e.code === "protected_base_branch";
}

async function call<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${DAEMON_URL}${path}`, { ...init, credentials: "include" });
  let body: unknown = {};
  try { body = await res.json(); } catch { body = {}; }
  if (!res.ok) {
    const code = body && typeof body === "object" ? (body as Record<string, unknown>).error : null;
    throw new PeterRefusal(refusalText(res.status, body), typeof code === "string" ? code : null);
  }
  return body as T;
}

/** File findings against `baseBranch`, the branch Bob tracks. Without
 *  `findingIds` every open finding nobody marked Solved is filed. */
export async function sendToPeter(
  project: string,
  args: { baseBranch: string; findingIds?: Array<string | number> },
): Promise<FromBobResult> {
  const r = await call<Partial<FromBobResult>>(`/projects/${encodeURIComponent(project)}/issues/from-bob`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(args),
  });
  return {
    repo: r.repo ?? "",
    bobBranch: r.bobBranch ?? "",
    baseBranch: r.baseBranch ?? args.baseBranch,
    created: Array.isArray(r.created) ? r.created : [],
    existing: Array.isArray(r.existing) ? r.existing : [],
    notFound: Array.isArray(r.notFound) ? r.notFound : [],
    solved: Array.isArray(r.solved) ? r.solved : [],
    malformed: typeof r.malformed === "number" ? r.malformed : 0,
    started: typeof r.started === "number" ? r.started : 0,
  };
}

export async function listBobIssues(project: string): Promise<PeterIssue[]> {
  const r = await call<{ issues?: unknown }>(`/projects/${encodeURIComponent(project)}/issues`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  const issues = Array.isArray(r.issues) ? (r.issues as PeterIssue[]) : [];
  return issues.filter((i) => i && typeof i === "object" && i.source === "bob");
}
